import type { VisualSeries } from '@lognai/plugin-analytics';
import type { ActiveInsight, AnalysisResult } from './api';
import { compactInsightDates } from './insight-dates';
import { formatPanelValue } from './MetricVisual';

export interface ChartSummary { text: string; source: 'ai' | 'calculated' }

/** Compare the displayed data; the server stale hint is based on dashboard defaults, not live filters. */
export function currentChartInsight(result: AnalysisResult, saved?: ActiveInsight): boolean {
  if (!saved || saved.revision.source === 'manual' || !saved.revision.context || !result.context) return false;
  const query = (trace: AnalysisResult['trace']) => trace.map(({ view, measures, dimensions, filters, rowCount }) => ({ view, measures, dimensions, filters, rowCount }));
  // An explanation can add supporting queries and facts. Require every displayed
  // query and fact to match, without rejecting those additional citations.
  const containsAll = (available: unknown[], required: unknown[]) => {
    const remaining = available.map(item => JSON.stringify(item));
    return required.every(item => {
      const index = remaining.indexOf(JSON.stringify(item));
      if (index < 0) return false;
      remaining.splice(index, 1);
      return true;
    });
  };
  return result.trace.length > 0 && result.evidence.length > 0
    && containsAll(query(saved.revision.trace), query(result.trace))
    && containsAll(saved.revision.evidence, result.evidence)
    && JSON.stringify(saved.revision.context.datasetVersions) === JSON.stringify(result.context.datasetVersions)
    && (saved.revision.context.timezone ?? 'UTC') === (result.context.timezone ?? 'UTC')
    && JSON.stringify(saved.revision.context.internalFreshness) === JSON.stringify(result.context.internalFreshness);
}

export function chartInsightSummary(result?: AnalysisResult, saved?: ActiveInsight, zeroIsPositive = false): ChartSummary {
  const calculated = (text: string): ChartSummary => ({ text: compactInsightDates(text, result?.analysis.timeDimension?.granularity), source: 'calculated' });
  if (!result) return calculated('The insight will appear when this chart finishes loading.');
  if (result.meta?.rowState === 'empty') return calculated('No data matches the current selection. Try a wider date range or fewer filters.');
  if (result.meta?.rowState === 'zero') return calculated(zeroIsPositive ? 'No current exposure detected in this selection.' : 'All visible values are zero for this selection.');
  if (currentChartInsight(result, saved) && saved!.revision.summary.trim()) {
    const full = compactInsightDates(saved!.revision.summary, result.analysis.timeDimension?.granularity).replace(/\s+/g, ' ').trim();
    const text = full.split(/(?<=[.!?])\s+/).slice(0, 2).map(sentence => sentence.trim()).join(' ').trim();
    return { text, source: saved!.revision.fallback || saved!.revision.source === 'calculated' ? 'calculated' : 'ai' };
  }
  const series = result.visual.series.find(item => item.points.some(point => Number.isFinite(point.value)));
  if (!series) return calculated('No numeric insight is available for this selection.');
  const points = series.points.filter(point => Number.isFinite(point.value));
  const format = (value: number, item: VisualSeries = series) => item.unit === 'days'
    ? `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value)} days`
    : formatPanelValue(value, item.unit, item.currency);
  if (result.analysis.baselineComparison) {
    const fact = (id: string) => {
      const raw = result.evidence.find(item => item.id === id)?.value;
      return raw !== undefined && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : null;
    };
    const baseline = fact('total-comparison.baseline'), current = fact('total-comparison.current');
    if (baseline !== null && current !== null) {
      const comparison = result.analysis.baselineComparison;
      const money = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: comparison.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
      const difference = current - baseline;
      const percent = baseline > 0 ? ` (${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Math.abs(difference) / baseline * 100)}%)` : '';
      const change = difference === 0 ? `matches ${comparison.baselineLabel}`
        : `is ${difference < 0 ? 'lower' : 'higher'} by ${money(Math.abs(difference))}${percent} than ${comparison.baselineLabel}`;
      return calculated(`${comparison.currentLabel} ${change} across the matched months. ${money(current)} versus ${money(baseline)}.`);
    }
    return calculated('The year-over-year comparison is unavailable for this selection.');
  }
  if (series.role === 'card') {
    return calculated(result.visual.series.filter(item => item.role === 'card' && Number.isFinite(item.points[0]?.value)).slice(0, 2)
      .map(item => `${item.name}: ${format(item.points[0].value, item)}.`).join(' '));
  }
  const target = result.visual.display?.target;
  if ((series.role === 'bullet' || result.visual.template === 'bullet') && typeof target === 'number' && Number.isFinite(target)) {
    const value = points[0].value;
    return calculated(`${series.name} is ${format(value)}, ${value < target ? 'below' : value > target ? 'above' : 'matching'} the ${format(target)} target.`);
  }
  if (points.length > 1 && (result.analysis.timeDimension || result.visual.display?.categoryOrder === 'month')) {
    const first = points[0], last = points[points.length - 1];
    return calculated(`${series.name} ${last.value > first.value ? 'increased' : last.value < first.value ? 'decreased' : 'was unchanged'} from ${format(first.value)} (${first.label}) to ${format(last.value)} (${last.label}).`);
  }
  if (points.length > 1) {
    const high = points.reduce((highest, point) => point.value > highest.value ? point : highest);
    const tied = points.filter(point => point.value === high.value).length;
    return calculated(tied === points.length ? `${series.name} is equal across all ${points.length} visible groups at ${format(high.value)}.`
      : `${high.label} ${tied > 1 ? 'is tied for' : 'has'} the highest ${series.name.toLowerCase()} at ${format(high.value)}.`);
  }
  return calculated(`${series.name}: ${format(points[0].value)}${points[0].label && points[0].label !== series.name ? ` (${points[0].label})` : ''}.`);
}
